import { AxiosProgressEvent } from "axios";
import instance from "../axiosConfig";
import { Download, DynamicDownload, DynamicDownloadRequest } from "./models";

export class DownloadService {
    private regionCode: string;

    constructor(regionCode: string) {
        this.regionCode = regionCode;
    }

    get = async (): Promise<Download[]> => {
        const response = await instance.get<Download[]>(`api/regions/${this.regionCode}/uploads`);

        return response.data;
    };

    retrieve = async (languageCode: string): Promise<Download[]> => {
        const response = await instance.get<Download[]>(
            `api/regions/${this.regionCode}/uploads/languages/${languageCode}`
        );

        return response.data;
    };

    create = async (
        languageCode: string,
        form: FormData,
        controller?: AbortController,
        onUploadProgress?: (event: AxiosProgressEvent) => void
    ): Promise<Download> => {
        const response = await instance.postForm<Download>(
            `api/regions/${this.regionCode}/uploads/languages/${languageCode}`,
            form,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                signal: controller?.signal,
                onUploadProgress,
            }
        );

        return response.data;
    };

    getDynamic = async (version: string): Promise<DynamicDownload[]> => {
        const response = await instance.get<DynamicDownload[]>(
            `api/regions/${this.regionCode}/dynamicuploads/${version}`
        );

        return response.data;
    };

    getAllDynamic = async (): Promise<DynamicDownload[]> => {
        const response = await instance.get<DynamicDownload[]>(`api/regions/${this.regionCode}/dynamicuploads`);

        return response.data;
    };

    downloadDynamic = async (version: string, downloadId: string, body: DynamicDownloadRequest): Promise<Blob> => {
        const response = await instance.post(
            `api/regions/${this.regionCode}/dynamicuploads/${version}/${downloadId}/download`,
            body,
            {
                responseType: "blob",
            }
        );

        return response.data;
    };

    createDynamic = async (
        version: string,
        form: FormData,
        controller?: AbortController,
        onUploadProgress?: (event: AxiosProgressEvent) => void
    ): Promise<DynamicDownload> => {
        const response = await instance.postForm<DynamicDownload>(
            `api/regions/${this.regionCode}/dynamicuploads/${version}`,
            form,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                signal: controller?.signal,
                onUploadProgress,
            }
        );

        return response.data;
    };

    update = async (downloadId: string, languageCode: string, data: Partial<Download>): Promise<Download> => {
        const response = await instance.post<Download>(
            `api/regions/${this.regionCode}/uploads/languages/${languageCode}/uploads/${downloadId}`,
            data
        );
        return response.data;
    };

    delete = async (downloadId: string): Promise<boolean> => {
        const response = await instance.delete<Download>(`api/regions/${this.regionCode}/uploads/${downloadId}`);

        return response.status === 204;
    };

    deleteDynamic = async (version: string, downloadId: string): Promise<boolean> => {
        const response = await instance.delete(
            `api/regions/${this.regionCode}/dynamicuploads/${version}/${downloadId}`
        );

        return response.status === 204;
    };
}
